import {
  createAsyncThunk,
  createSelector,
  createSlice,
} from '@reduxjs/toolkit';
import axios from 'axios';
import JSZip from 'jszip';
import { ok } from 'node:assert';
import apiRequestHeaders from '../../common/apiRequestHeaders';
import { RootState } from '../../app/store';
import {
  CARETAKER_ROLES,
  CUSTOMER_ROLE,
  LOADING_STATUS,
  OFFICER_ROLES,
  OWNER_ROLE,
  TRAVELAGENCY_ROLE,
} from '../../common/constants';
import userService, {
  addaddressData,
  fetchAccommodationAccounts,
  fetchAccountDetailsFromDb,
  fetchAccountRolesFromDb,
  fetchAllAccountsFromDb,
  fetchAllCustomerGroups,
  fetchAllIdentityUsers,
  fetchAllMaintainerBookingsDetails,
  fetchAllOwnerBookingsDetails,
  fetchAllRoles,
  fetchBankSettings,
  fetchBookingNoteSettings,
  fetchCustomerBookings,
  fetchCustomerGroupDetails,
  fetchCustomerSettings,
  fetchDefaultCustomerGroup,
  fetchIdentityAddressDetails,
  fetchIdentityUserDetails,
  fetchIdentityUserRoles,
  fetchMyAgentNumber,
  fetchOfficerSettings,
  updateOfficerSettings,
  fetchOmaLiittymaSettings,
  fetchOwnerAutoCompleteOptions,
  fetchOwnerMaintainerAccounts,
  fetchTarmoSettings,
  fetchUserDetailsByIdentityId,
  fetchVatSettings,
  fetchPersonalIdentityData,
  savePersonalIdentity,
  deletePersonalIdentity,
  createIdentityVerificationLink,
  markAccountDeleted,
  updateAccountData,
  updateaddressData,
  updateBankSettings,
  updateBookingNoteSettings,
  updateCustomerGroupData,
  updateCustomerSettings,
  updateOmaLiittymaSettings,
  updateTarmoSettings,
  updateVatSettings,
} from '../../common/api/userService';

import {
  InitialPaginationResponse,
  PaginationParameters,
  PaginationResponse,
  PopulatePagination,
} from '../../common/types/pagination.types';
import { TranslationItem } from '../../common/types/translations.types';
import { fetchAccommodationAutoCompleteOptions } from '../../common/api/accommodationsService';
import { BookingContainer } from '../bookings/bookingsSlice';

export interface OwnerAutoCompleteOption {
  ownerId: string;
  ownerNumber: number;
  firstName?: string;
  lastName?: string;
}
export interface CustomerSettingsUpdateData {
  id: string;
  accountId: string;
  customerGroupId: string;
  customerNumber: number;
  isDeleted: boolean;
  officeFee: boolean;
  marketingAuthorizationViaEmail: boolean;
  fullEmailMarketingAllowed: boolean;
  marketingAuthorizationViaSms: boolean;
  travelAgencyCommission: number;
  travelAgencyVat: number;
  discountVatP: number;
  isDomestic: boolean;
  useCancelInsurance: boolean;
  isSkiCenterCustomer: boolean;
  isAdvanceInvoiceAllowed: boolean;
  invoiceByMail: boolean;
  invoiceByEmail: boolean;
  invoiceByInternet: boolean;
  domesticPriceMultiplier: number;
  bookingOfficeTenantId: string;
  advanceInvoiceDueDateOffsetDays: number;
  finalInvoiceDueDateOffsetDays: number;
  additionalInfo: string;
  complaints: string;
  billingLocale: string;
  isRegularCustomer: boolean;
}

export interface CustomerSettingsResponse {
  id: string;
  accountId: string;
  customerGroupId: string;
  customerNumber: number;
  isDeleted: boolean;
  officeFee: boolean;
  marketingAuthorizationViaEmail: boolean;
  fullEmailMarketingAllowed: boolean;
  marketingAuthorizationViaSms: boolean;
  travelAgencyCommission: number;
  travelAgencyVat: number;
  discountVatP: number;
  isDomestic: boolean;
  useCancelInsurance: boolean;
  isSkiCenterCustomer: boolean;
  isAdvanceInvoiceAllowed: boolean;
  invoiceByMail: boolean;
  invoiceByEmail: boolean;
  invoiceByInternet: boolean;
  domesticPriceMultiplier: number;
  bookingOfficeTenantId: string;
  advanceInvoiceDueDateOffsetDays: number;
  finalInvoiceDueDateOffsetDays: number;
  additionalInfo: string;
  complaints: string;
  billingLocale: string;
  isRegularCustomer: boolean;
}
export interface CustomerGroupTranslationsdata {
  Id: string;
  AccountId: string;
  CustomerGroupId: string;
  CustomerNumber: number;
  IsDeleted: boolean;
  OfficeFee: boolean;
  MarketingAuthorizationViaEmail: boolean;
  FullEmailMarketingAllowed: boolean;
  MarketingAuthorizationViaSms: boolean;
  TravelAgencyCommission: number;
  TravelAgencyVat: number;
  DiscountVatP: number;
  IsDomestic: boolean;
  useCancelInsurance: boolean;
  IsSkiCenterCustomer: boolean;
  IsAdvanceInvoiceAllowed: boolean;
  InvoiceByMail: boolean;
  InvoiceByEmail: boolean;
  InvoiceByInternet: boolean;
  DomesticPriceMultiplier: number;
  BookingOfficeTenantId: string;
  AdvanceInvoiceDueDateOffsetDays: number;
  FinalInvoiceDueDateOffsetDays: number;
  AdditionalInfo: string;
  Complaints: string;
  BillingLocale: string;
  IsRegularCustomer: boolean;
}

export interface CustomerGroupUpdateData {
  id: string;
  regularCustomerGroup: boolean;
  travelAgencyCustomerGroup: boolean;
  wintimeCustomerGroupNumber: number;
  wintimeCustomerGroupId: string;
  defaultCustomerGroup: boolean;
  automaticWelcomesDisabled: boolean;
  translations: CustomerGroupTranslationsdata[];
}
export interface CustomerGroupDetailsTranslations {
  keyId: string;
  locale: string;
  text: string;
  id: string;
  customerGroupId: string;
}
export interface CustomerGroupDetailsData {
  id: string;
  regularCustomerGroup: boolean;
  travelAgencyCustomerGroup: boolean;
  wintimeCustomerGroupNumber: number;
  wintimeCustomerGroupId: string;
  defaultCustomerGroup: boolean;
  automaticWelcomesDisabled: boolean;
  translations: CustomerGroupDetailsTranslations[];
}

export interface AddressUpdateData {
  id: string;
  isPrimary: boolean;
  description: string;
  postCity: string;
  postStreetAddress: string;
  additionalAddressLine: string;
  postCode: string;
  countryId: string;
}

export interface AzureFileItemDto {
  name: string;
  lastModified: string;
}

export interface CustomerGroupListItemTranslations extends TranslationItem {
  id: string;
}

export interface CustomerGroupListItem {
  id: string;
  translations: CustomerGroupListItemTranslations[];
}
export interface PhoneUpdateData {
  id: string;
  isPrimary: boolean;
  bookingNoteBySms: boolean;
  description: string;
  typeId: string;
  phoneNumber: string;
}

export interface EmailUpdateData {
  id: string;
  isPrimary: boolean;
  isBookingNoteEmail: boolean;
  description: string;
  emailAddress: string;
}
export interface ContactUpdateData {
  firstName: string;
  lastName: string;
  www: string;
  description: string;
  companyName: string;
  additionalInformation: string;
  addresses: AddressUpdateData[];
  phones: PhoneUpdateData[];
  emails: EmailUpdateData[];
}
export interface AccountUpdateData {
  id: string;
  contactUpdateData: ContactUpdateData;
  accountsRoles: string[];
  travelAgencyDomains?: string[];
}
export interface IdentityUserItem {
  id: string;
  userName: string;
  normalizedUserName: string;
  email: string;
  normalizedEmail: string;
  emailConfirmed: boolean;
  passwordHash: string;
  securityStamp: string;
  concurrencyStamp: string;
  phoneNumber: string;
  phoneNumberConfirmed: boolean;
  twoFactorEnabled: boolean;
  lockoutEnd: string;
  lockoutEnabled: boolean;
  accessFailedCount: number;
}

export interface RolesFilter {
  pageNumber: number;
  pageSize: number;
  searchString: string;
  culture: string;
}

export interface AccountsFilter extends PaginationParameters {
  searchString: string;
  culture: string;
  roleIds?: string[];
  isActive?: boolean;
  createdFrom?: Date;
  createdTo?: Date;
  updatedFrom?: Date;
  updatedTo?: Date;
  bookingCreatedFrom?: Date;
  bookingCreatedTo?: Date;
  bookingArrivalFrom?: Date;
  bookingArrivalTo?: Date;
  bookingDepartureFrom?: Date;
  bookingDepartureTo?: Date;
  companyId?: string;
  paysVat?: boolean;
  hasPersonalIdentityCode?: boolean;
  marketingAuthorizationViaEmail?: boolean;
  fullEmailMarketingAllowed?: boolean;
  marketingAuthorizationViaSms?: boolean;
  invoiceByInternet?: boolean;
  countryId?: string;
  customerGroupId?: string;
}

export interface OwnerBookingsFilter extends PaginationParameters {
  ownerId: string;
}

export interface MaintainerBookingsFilter extends PaginationParameters {
  maintainerId: string;
}

export interface GroupsFilter extends PaginationParameters {
  SearchString: string;
  Culture: string;
}
export interface DefaultGroupFilter {
  BookingType: number;
  Locale: string;
}
export interface AccountListItem {
  id: string;
  accountId: string;
  firstName: string;
  hasUserLogin: boolean;
  lastName: string;
  userName: string;
  hasBankIban: boolean;
  hasBankIbanLength: boolean;
  roleName: string;
  hasPersonalIdentityCode: boolean;
}

export interface AccountExtendedListItem extends AccountListItem {
  caretakerNumber: string;
  companyName: string;
  contactPersonName: string;
  customerNumber: string;
  ownerNumber: string;
  officerNumber: string;
  travelAgencyNumber: string;
  phoneNumber: string;
  postCity: string;
  postStreetAddress: string;
  isDeleted: boolean;
}

export interface RoleListItem {
  name: string;
  id: string;
  description: string;
}

export interface Rolesresponse {
  rolesList: RoleListItem[];
}
export interface ExtraOrderLine {
  id: string;
  itemName: string;
  price: number;
  purchasePrice: number;
}
export interface GetCustomerBookingsResponse {
  /**
   * Gets the identifier.
   */
  id: string;

  /**
   * Gets the booking number.
   */
  bookingNumber: number;

  /**
   * Gets the is cancelled.
   */
  isCancelled: boolean;

  /**
   * Gets the first name of the payer.
   */
  payerFirstName?: string;

  /**
   * Gets the last name of the payer.
   */
  payerLastName?: string;

  /**
   * Gets PayerId of the payer.
   */
  payerId?: string;

  /**
   * Gets StartDate of the booking.
   */
  startDate?: Date;

  /**
   * Gets EndDate of the booking.
   */
  endDate?: Date;

  /**
   * Gets ItemName of the booking.
   */
  itemName?: string;
  bookingType: number;
  isConfirmed: boolean;
  price: number;
  purchasePrice: number;
  additionalOrderItems: ExtraOrderLine[];
}
export interface UserDetails {
  id: string;
  userNumber: number;
  userType: string;
  firstName: string;
  lastName: string;
  primaryEmail: string;
  primaryPhone: string;
}

export interface PhoneResponse {
  id: string;
  isPrimary: boolean;
  bookingNoteBySms: boolean;
  description: string;
  typeId: string;
  phoneNumber: string;
}

export interface EmailResponse {
  id: string;
  isPrimary: boolean;
  isBookingNoteEmail: boolean;
  description: string;
  emailAddress: string;
}

export interface OmaLiittymaResponse {
  hideBillOwnerData: boolean;
  lastRequestDate: string;
  accountId: string;
  id: string;
  ownerNumber: number;
  caretakerNumber: number;
  directLinkPath: string;
}

export interface BookingNoteSettingsResponse {
  id: string;
  accountId: string;
  bookingNoteBySms: boolean;
  bookingNoteWeekly: boolean;
  noBookingNote: boolean;
  noMassSms: boolean;
  bookingNoteImmediatelly: boolean;
  bookingNoteByMail: boolean;
  bookingNoteByEmail: boolean;
}

export interface VatSettingsResponse {
  id: string;
  accountId: string;
  paysVat: boolean;
  yCode: string;
  hetu: string;
  otherVatCode: string;
  purchaseVatId: string;
}

export interface TarmoSettingsResponse {
  id: string;
  tarmoAPIAddress: string;
  tarmoAPIKey: string;
  accountId: string;
}

export interface BookingNoteSettingsUpdateData {
  id: string;
  accountId: string;
  bookingNoteBySms: boolean;
  bookingNoteWeekly: boolean;
  noMassSms: boolean;
  noBookingNote: boolean;
  bookingNoteImmediatelly: boolean;
  bookingNoteByMail: boolean;
  bookingNoteByEmail: boolean;
}

export interface VatSettingsUpdateData {
  id: string;
  accountId: string;
  paysVat: boolean;
  yCode: string;
  hetu: string;
  otherVatCode: string;
  purchaseVatId: string;
}
export interface BankSettingsUpdateData {
  id: string;
  accountId: string;
  iban: string;
  bic: string;
}

export interface TarmoSettingsUpdateData {
  id: string;
  tarmoAPIAddress: string;
  tarmoAPIKey: string;
}

export interface BankSettingsResponse {
  id: string;
  accountId: string;
  iban: string;
  bic: string;
}

export interface OfficerResponse {
  id: string;
  accountId: string;
  officerNumber: string;
  agentNumber?: number | null;
}

export interface UpdateOfficerSettingsData {
  accountId: string;
  agentNumber: number | null;
}

export interface OmaLiittymaUpdateData {
  hideBillOwnerData: boolean;
  lastRequestDate: string;
  accountId: string;
  id: string;
  ownerNumber: number;
  caretakerNumber: number;
  directLinkPath: string;
}

export interface GetCustomerBookingsQuery {
  /**
   * The page number.
   */
  pageNumber: number;

  /**
   * The size of the page.
   */
  pageSize: number;

  /**
   * The customer identifier.
   */
  customerId: string;
}
export interface AddressResponse {
  id: string;
  isPrimary: boolean;
  description: string;
  postCity: string;
  postStreetAddress: string;
  additionalAddressLine: string;
  postCode: string;
  countryId: string;
}
export interface BookingItem {
  id: string;
  bookingType: number;
  bookingNumber: number;
  payerFirstName: string;
  payerLastName: string;
  startDate: string;
  startDateString: string;
  endDate: string;
  endDateString: string;
  adults: number;
  children: number;
  isCancelled: boolean;
  itemName: string;
  isConfirmed: boolean;
  additionalOrderItems: ExtraOrderLine[];
  price: number;
  purchasePrice: number;
}
export interface UpdateAddressData {
  id: string;
  accommodationId: string;
  isPrimary: boolean;
  description: string;
  postCity: string;
  postStreetAddress: string;
  additionalAddressLine: string;
  postCode: string;
  countryId: string;
}

export interface AccountDetailsFromDb {
  id: string;
  www: string;
  description: string;
  companyName: string;
  additionalInformation: string;
  subjectId: string;
  firstName: string;
  lastName: string;
  phones: PhoneResponse[];
  emails: EmailResponse[];
  addresses: AddressResponse[];
  travelAgencyDomains?: string[];
}

export interface IdentityState {
  wintimeFile: {
    blobUrl: string;
    fileName: string;
  };
  downloadwintimeFileStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;
  ownerBookings: BookingItem[];
  customerBookings: GetCustomerBookingsResponse[];
  customerBookingsStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;
  customerBookingsPagination: PaginationResponse;
  personalIdentityData: {
    hasPersonalIdentityCode: boolean;
    issuedAt?: string;
    updatedAt?: string;
    bankTupasId?: string;
  };
  personalIdentityLoadingStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;
  maintainerBookings: BookingItem[];
  ownerBookingsStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;
  maintainerBookingsStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;
  customerSettingsData: CustomerSettingsResponse;
  customerSettingsLoadingStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;
  addressDetails: AddressResponse;
  omaLiittymaData: OmaLiittymaResponse;
  bankSettingsData: BankSettingsResponse;
  tarmoSettingsData: TarmoSettingsResponse;
  customerGroupItems: CustomerGroupListItem[];
  defaultCustomerGroupItem: CustomerGroupListItem;
  customerGroupDetailsStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;
  customerGroupDetailsData: CustomerGroupDetailsData;
  customerGroupPagination: PaginationResponse;
  customerGroupsStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;
  officerSettings: OfficerResponse;
  officerSettingsLoadingStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;
  bankSettingsLoadingStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;
  tarmoSettingsLoadingStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;
  bookingNoteSettingsData: BookingNoteSettingsResponse;
  bookingNoteSettingsLoadingStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;
  vatSettingsData: VatSettingsResponse;
  vatSettingsLoadingStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;
  omaLiittymaLoadingStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;
  addressLoadingStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;

  identityUsers: IdentityUserItem[];
  identityDetails: IdentityUserItem;
  userDetails: UserDetails;
  accountDetails: AccountDetailsFromDb;
  accountRoles: string[];
  accountList: AccountExtendedListItem[];
  customerList: AccountExtendedListItem[];
  accommodationAccountList: AccountListItem[];
  accommodationAccountListLoadingStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;
  ownerMaintainerAccountList: AccountListItem[];
  ownerMaintainerAccountListLoadingStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;
  identityUserRoles: [];
  allRoles: Rolesresponse;
  identityUsersStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;

  userStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;

  rolesStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;

  accountRolesLoadingStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;

  accountListLoadingStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;

  maintainerAccountListLoadingStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;

  accountDetailsLoadingStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;

  accountPagination: PaginationResponse;
  ownerBookingsPagination: PaginationResponse;
  maintainerBookingsPagination: PaginationResponse;
  ownerMaintainerPagination: PaginationResponse;
  customerPagination: PaginationResponse;
}

const initialState: IdentityState = {
  wintimeFile: {
    blobUrl: '',
    fileName: '',
  },
  downloadwintimeFileStatus: LOADING_STATUS.IDLE,
  ownerBookings: [],
  maintainerBookings: [],
  maintainerBookingsStatus: LOADING_STATUS.IDLE,
  ownerBookingsStatus: LOADING_STATUS.IDLE,
  customerSettingsLoadingStatus: LOADING_STATUS.IDLE,
  customerSettingsData: null,
  addressLoadingStatus: LOADING_STATUS.IDLE,
  addressDetails: null,
  identityUsers: [],
  omaLiittymaData: null,
  officerSettings: null,
  customerGroupDetailsStatus: LOADING_STATUS.IDLE,
  customerGroupDetailsData: {
    id: '',
    regularCustomerGroup: null,
    travelAgencyCustomerGroup: false,
    wintimeCustomerGroupNumber: null,
    wintimeCustomerGroupId: null,
    defaultCustomerGroup: null,
    automaticWelcomesDisabled: false,
    translations: [],
  },
  customerGroupItems: [],
  defaultCustomerGroupItem: null,
  customerGroupPagination: InitialPaginationResponse,
  customerGroupsStatus: LOADING_STATUS.IDLE,
  officerSettingsLoadingStatus: LOADING_STATUS.IDLE,
  bankSettingsLoadingStatus: LOADING_STATUS.IDLE,
  bankSettingsData: null,
  tarmoSettingsLoadingStatus: LOADING_STATUS.IDLE,
  tarmoSettingsData: null,
  omaLiittymaLoadingStatus: LOADING_STATUS.IDLE,
  bookingNoteSettingsData: null,
  bookingNoteSettingsLoadingStatus: LOADING_STATUS.IDLE,
  vatSettingsData: null,
  vatSettingsLoadingStatus: LOADING_STATUS.IDLE,
  maintainerAccountListLoadingStatus: LOADING_STATUS.IDLE,
  allRoles: null,
  identityUsersStatus: LOADING_STATUS.IDLE,
  identityDetails: null,
  userStatus: LOADING_STATUS.IDLE,
  rolesStatus: LOADING_STATUS.IDLE,
  userDetails: null,
  identityUserRoles: [],
  accountRoles: [],
  accountList: [],
  customerList: [],
  accountDetails: null,
  accountDetailsLoadingStatus: LOADING_STATUS.IDLE,
  accountListLoadingStatus: LOADING_STATUS.IDLE,
  accountRolesLoadingStatus: LOADING_STATUS.IDLE,
  accountPagination: InitialPaginationResponse,
  customerPagination: InitialPaginationResponse,
  ownerBookingsPagination: InitialPaginationResponse,
  maintainerBookingsPagination: InitialPaginationResponse,
  accommodationAccountList: [],
  accommodationAccountListLoadingStatus: LOADING_STATUS.IDLE,
  ownerMaintainerAccountList: [],
  ownerMaintainerAccountListLoadingStatus: LOADING_STATUS.IDLE,
  ownerMaintainerPagination: InitialPaginationResponse,
  customerBookingsStatus: LOADING_STATUS.IDLE,
  customerBookings: [],
  customerBookingsPagination: InitialPaginationResponse,
  personalIdentityData: null,
  personalIdentityLoadingStatus: LOADING_STATUS.IDLE,
};

export const getCustomerSettingsAsync = createAsyncThunk(
  'users/getCustomerSettingsAsync',
  async (accountId: string, { rejectWithValue }) => {
    try {
      return await fetchCustomerSettings(accountId);
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const UpdateCustomerSettinsAsync = createAsyncThunk(
  'accommodations/UpdateCustomerSettinsAsync',
  async (
    customerSettingsData: CustomerSettingsUpdateData,
    { rejectWithValue },
  ) => {
    try {
      const response = await updateCustomerSettings(customerSettingsData);
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);
export const getAllIdentityUsersAsync = createAsyncThunk(
  'users/getAllIdentityUsers',
  /* TODO Replace any/ CORE-1006 */
  /* eslint-disable  @typescript-eslint/no-explicit-any */
  async (_: any, { rejectWithValue }) => {
    try {
      const response = await fetchAllIdentityUsers();
      return response;
    } catch (error) {
      // Use custom error response
      return rejectWithValue(error.response.data);
    }
  },
);

export const getAllAccountsFromDbAsync = createAsyncThunk(
  'users/getAllAccountsFromDbAsync',
  async (filterCriteria: AccountsFilter, { rejectWithValue }) => {
    try {
      // Do some cheking;
      const response = await fetchAllAccountsFromDb(filterCriteria);
      return response;
    } catch (error) {
      // Use custom error response
      return rejectWithValue(error.response);
    }
  },
);

export const getOwnerMaintainerAccountsAsync = createAsyncThunk(
  'users/getOwnerMaintainerAccountsAsync',
  async (filterCriteria: AccountsFilter, { rejectWithValue }) => {
    try {
      // Do some cheking;
      const response = await fetchOwnerMaintainerAccounts(filterCriteria);
      return response;
    } catch (error) {
      // Use custom error response
      return rejectWithValue(error.response);
    }
  },
);

export const getOwnerAutoCompleteOptionsAsync = createAsyncThunk(
  'users/getOwnerAutoCompleteOptions',
  async (searchTerm: string, { rejectWithValue }) => {
    try {
      const response = await fetchOwnerAutoCompleteOptions(searchTerm);
      return response.data;
    } catch (error) {
      // Use custom error response
      return rejectWithValue(error.response.data);
    }
  },
);
export const getAccommodationAccountsFromDbAsync = createAsyncThunk(
  'users/getAccommodationAccountsFromDbAsync',
  async (filterCriteria: string[], { rejectWithValue }) => {
    try {
      // Do some cheking;
      const response = await fetchAccommodationAccounts(filterCriteria);
      return response;
    } catch (error) {
      // Use custom error response
      return rejectWithValue(error.response);
    }
  },
);
export const getAllCustomersFromDbAsync = createAsyncThunk(
  'users/getAllCustomersFromDbAsync',
  async (filterCriteria: AccountsFilter, { rejectWithValue }) => {
    try {
      // Do some cheking;
      const response = await fetchAllAccountsFromDb(filterCriteria);
      return response;
    } catch (error) {
      // Use custom error response
      return rejectWithValue(error.response);
    }
  },
);
export const getIdentityUserDetailsAsync = createAsyncThunk(
  'users/getIdentityUserDetails',
  async (identityId: string, { rejectWithValue }) => {
    try {
      const response = await fetchIdentityUserDetails(identityId);
      return response;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const getAllOwnerBookingsAsync = createAsyncThunk(
  'users/getOwnerUserBookings',
  async (ownerBookingsFilter: OwnerBookingsFilter, { rejectWithValue }) => {
    try {
      const response = await fetchAllOwnerBookingsDetails(ownerBookingsFilter);
      return response;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const getAllMaintainerBookingsAsync = createAsyncThunk(
  'users/getMaintainerUserBookings',
  async (
    maintainerBookingsFilter: MaintainerBookingsFilter,
    { rejectWithValue },
  ) => {
    try {
      const response = await fetchAllMaintainerBookingsDetails(
        maintainerBookingsFilter,
      );
      return response;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const getIdentityAddressDetailsAsync = createAsyncThunk(
  'users/getIdentityAddressDetailsAsync',
  async (addressId: string, { rejectWithValue }) => {
    try {
      const response = await fetchIdentityAddressDetails(addressId);
      return response;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const getUserDetailsByIdentityIdAsync = createAsyncThunk(
  'users/getUserDetailsByIdentityId',
  async (identityId: string, { rejectWithValue }) => {
    try {
      return await fetchUserDetailsByIdentityId(identityId);
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const getAcountDetailsFromDbAsync = createAsyncThunk(
  'users/getAcountDetailsFromDbAsync',
  async (accountId: string, { rejectWithValue }) => {
    try {
      return await fetchAccountDetailsFromDb(accountId);
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const getPersonalIdentityDataAsync = createAsyncThunk(
  'users/getPersonalIdentityDataAsync',
  async (accountId: string, { rejectWithValue }) => {
    try {
      return await fetchPersonalIdentityData(accountId);
    } catch (error) {
      return rejectWithValue(error.response?.data ?? error);
    }
  },
);

export const savePersonalIdentityAsync = createAsyncThunk(
  'users/savePersonalIdentityAsync',
  async (
    params: { accountId: string; personalIdentityCode: string },
    { rejectWithValue },
  ) => {
    try {
      return await savePersonalIdentity({
        accountId: params.accountId,
        personalIdentityCode: params.personalIdentityCode,
      });
    } catch (error) {
      return rejectWithValue(error.response?.data ?? error);
    }
  },
);

export const deletePersonalIdentityAsync = createAsyncThunk(
  'users/deletePersonalIdentityAsync',
  async (accountId: string, { rejectWithValue }) => {
    try {
      return await deletePersonalIdentity(accountId);
    } catch (error) {
      return rejectWithValue(error.response?.data ?? error);
    }
  },
);

export const createIdentityVerificationLinkAsync = createAsyncThunk(
  'users/createIdentityVerificationLinkAsync',
  async (
    params: { accountId: string; returnUrl: string },
    { rejectWithValue },
  ) => {
    try {
      return await createIdentityVerificationLink({
        accountId: params.accountId,
        returnUrl: params.returnUrl,
      });
    } catch (error) {
      return rejectWithValue(error.response?.data ?? error);
    }
  },
);

export const resetAccountDetailsAsync = createAsyncThunk(
  'users/setAccountDetails',
  async (accountDetails: AccountDetailsFromDb) => {
    return accountDetails;
  },
);
export const getOmaLiittymaSettingsAsync = createAsyncThunk(
  'users/getOmaLiittymaSettingsAsync',
  async (accountId: string, { rejectWithValue }) => {
    try {
      return await fetchOmaLiittymaSettings(accountId);
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const getMyAgentNumberAsync = createAsyncThunk(
  'users/getMyAgentNumberAsync',
  async (_, { rejectWithValue }) => {
    try {
      return await fetchMyAgentNumber();
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  },
);

export const getOfficerSettingsAsync = createAsyncThunk(
  'users/getOfficerSettingsAsync',
  async (accountId: string, { rejectWithValue }) => {
    try {
      return await fetchOfficerSettings(accountId);
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const updateOfficerSettingsAsync = createAsyncThunk(
  'users/updateOfficerSettingsAsync',
  async (data: UpdateOfficerSettingsData, { rejectWithValue }) => {
    try {
      return await updateOfficerSettings(data);
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const getBankSettingsAsync = createAsyncThunk(
  'users/getBankSettingsAsync',
  async (accountId: string, { rejectWithValue }) => {
    try {
      return await fetchBankSettings(accountId);
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const getTarmoSettingsAsync = createAsyncThunk(
  'users/getTarmoSettingsAsync',
  async (accountId: string, { rejectWithValue }) => {
    try {
      return await fetchTarmoSettings(accountId);
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const getBookingNoteSettingsAsync = createAsyncThunk(
  'users/getBookingNoteSettingsAsync',
  async (accountId: string, { rejectWithValue }) => {
    try {
      return await fetchBookingNoteSettings(accountId);
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const getAllCustomerGroupsAsync = createAsyncThunk(
  'customers/getAllCustomerGroups',
  async (filterCriteria: GroupsFilter, { rejectWithValue }) => {
    try {
      // Do some cheking;
      const response = await fetchAllCustomerGroups(filterCriteria);
      return response;
    } catch (error) {
      // Use custom error response
      return rejectWithValue(error.response);
    }
  },
);

export const getDefaultCustomerGroupAsync = createAsyncThunk(
  'customers/getDefaultCustomerGroupAsync',
  async (filterCriteria: DefaultGroupFilter, { rejectWithValue }) => {
    try {
      // Do some cheking;
      const response = await fetchDefaultCustomerGroup(filterCriteria);
      return response.result;
    } catch (error) {
      // Use custom error response
      return rejectWithValue(error.response);
    }
  },
);

export const getCustomerGroupDetailsAsync = createAsyncThunk(
  'customers/getCustomerGroupDetails',
  async (customerGroupId: string, { rejectWithValue }) => {
    try {
      const response = await fetchCustomerGroupDetails(customerGroupId);
      return response.result;
    } catch (error) {
      // Use custom error response
      return rejectWithValue(error.response.data);
    }
  },
);

export const getVatSettingsAsync = createAsyncThunk(
  'users/getVatSettingsAsync',
  async (accountId: string, { rejectWithValue }) => {
    try {
      return await fetchVatSettings(accountId);
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const getCustomerBookingsAsync = createAsyncThunk(
  'users/getCustomerBookingsAsync',
  async (query: GetCustomerBookingsQuery, { rejectWithValue }) => {
    try {
      return await fetchCustomerBookings(query);
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);
export const getIdentityUserRolesAsync = createAsyncThunk(
  'users/getIdentityUserRolesAsync',
  async (identityId: string, { rejectWithValue }) => {
    try {
      return await fetchIdentityUserRoles(identityId);
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);
export const getAccountRolesFromDbAsync = createAsyncThunk(
  'users/getUserRolesAsync',
  async (accountId: string, { rejectWithValue }) => {
    try {
      return await fetchAccountRolesFromDb(accountId);
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const getAllRolesAsync = createAsyncThunk(
  'users/getAllRolesAsync',
  async (rolesFilter: RolesFilter, { rejectWithValue }) => {
    try {
      return await fetchAllRoles(rolesFilter);
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const addAddressDataAsync = createAsyncThunk(
  'accommodations/updateAccommodationAttributeData',
  async (addressData: UpdateAddressData, { rejectWithValue }) => {
    try {
      const response = await addaddressData(addressData);
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const updateCustomerGroup = createAsyncThunk(
  'accommodations/updateCustomerGroup',
  async (customerGroupData: CustomerGroupUpdateData, { rejectWithValue }) => {
    try {
      const response = await updateCustomerGroupData(customerGroupData);
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);
export const updateAddressDataAsync = createAsyncThunk(
  'accommodations/updateAddressDataAsync',
  async (addressData: UpdateAddressData, { rejectWithValue }) => {
    try {
      const response = await updateaddressData(addressData);
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const UpdateOmaLiittymaSettinsAsync = createAsyncThunk(
  'accommodations/updateOmaliittymaSettings',
  async (addressData: OmaLiittymaUpdateData, { rejectWithValue }) => {
    try {
      const response = await updateOmaLiittymaSettings(addressData);
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const UpdateBookingNoteSettinsAsync = createAsyncThunk(
  'accommodations/UpdateBookingNoteSettinsAsync',
  async (
    bookingNoteSettingsData: BookingNoteSettingsUpdateData,
    { rejectWithValue },
  ) => {
    try {
      const response = await updateBookingNoteSettings(bookingNoteSettingsData);
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const UpdateVatSettinsAsync = createAsyncThunk(
  'accommodations/UpdateVatSettinsAsync',
  async (vatSettingsData: VatSettingsUpdateData, { rejectWithValue }) => {
    try {
      const response = await updateVatSettings(vatSettingsData);
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const UpdateTarmoSettinsAsync = createAsyncThunk(
  'accommodations/UpdateTarmoSettinsAsync',
  async (tarmoSettingsData: TarmoSettingsUpdateData, { rejectWithValue }) => {
    try {
      const response = await updateTarmoSettings(tarmoSettingsData);
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const UpdateBankSettinsAsync = createAsyncThunk(
  'accommodations/UpdateBankSettinsAsync',
  async (bankSettingsData: BankSettingsUpdateData, { rejectWithValue }) => {
    try {
      const response = await updateBankSettings(bankSettingsData);
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const updateAccountDataAsync = createAsyncThunk(
  'accommodations/updateAccountDataAsync',
  async (accountUpdateData: AccountUpdateData, { rejectWithValue }) => {
    try {
      const response = await updateAccountData(accountUpdateData);
      return response;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);
export const markAccountDeletedAsyncAsync = createAsyncThunk(
  'accommodations/markAccountDeletedAsyncAsync',
  async (accountUpdateData: AccountUpdateData, { rejectWithValue }) => {
    try {
      const response = await markAccountDeleted(accountUpdateData);
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

interface AddLoginAsyncPayload {
  accountId: string;
}

export const addUserPrivateLoginAsync = createAsyncThunk(
  'auth/addUserPrivateLogin',
  async ({ accountId }: AddLoginAsyncPayload, { rejectWithValue }) => {
    try {
      await userService.addUserPrivateLoginAsync(accountId);
      return accountId;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const deleteUserLoginAsync = createAsyncThunk(
  'auth/deleteUserLogin',
  async ({ accountId }: AddLoginAsyncPayload, { rejectWithValue }) => {
    try {
      await userService.deleteUserLoginAsync(accountId);
      return accountId;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const restoreUserLoginAsync = createAsyncThunk(
  'auth/deleteUserLogin',
  async ({ accountId }: AddLoginAsyncPayload, { rejectWithValue }) => {
    try {
      await userService.restoreUserAsync(accountId);
      return accountId;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const downloadFilesFromWintimeFolderAsync = createAsyncThunk(
  'auth/downloadFilesFromWintimeFolder',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        '/accountings/accounting/downloadfilesfromwintimefolder',
        {
          headers: {
            'Content-Type': 'application/zip',
            ...apiRequestHeaders.csrfHeader(),
            ...apiRequestHeaders.activeSiteHeader(),
          },
          responseType: 'blob',
        },
      );

      const contentDisposition = response.headers['content-disposition'];
      let fileName = 'default.zip';
      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(
          /filename\*?=['"]?([^;\n]+)['"]?/,
        );
        if (fileNameMatch && fileNameMatch.length > 1) {
          [, fileName] = fileNameMatch;
        }
      }

      const blobUrl = URL.createObjectURL(response.data);
      return { blobUrl, fileName };
    } catch (error) {
      return rejectWithValue(false);
    }
  },
);
// Helper function to extract filename
const getFileName = (contentDisposition: string): string => {
  if (!contentDisposition) return 'default.zip';

  const fileNameMatch = contentDisposition.match(
    /filename\*?=['"]?([^;\n]+)['"]?/,
  );
  return fileNameMatch && fileNameMatch.length > 1
    ? fileNameMatch[1]
    : 'default.zip';
};
interface WintimeFolderResponse {
  latestModified: string; // ISO date string from the API
  fileName: string;
}

export const downloadFakeWintimeFolderAsync = createAsyncThunk<
  WintimeFolderResponse, // Success type
  void, // Argument type
  { rejectValue: any } // ThunkAPI configuration
>('yourSlice/downloadFakeWintimeFolder', async (_, { rejectWithValue }) => {
  try {
    const response = await axios.get(
      '/accountings/accounting/downloadfilesfromwintimefolder',
      {
        headers: {
          'Content-Type': 'application/zip',
          ...apiRequestHeaders.csrfHeader(),
          ...apiRequestHeaders.activeSiteHeader(),
        },
        responseType: 'blob',
      },
    );

    if (response.data.size > 0) {
      const zip = await JSZip.loadAsync(response.data);

      let latestDate = null;
      let hasEntries = false;

      zip.forEach((relativePath, zipEntry) => {
        if (!zipEntry.dir) {
          hasEntries = true;
          const fileDate = new Date(zipEntry.date);
          if (!latestDate || fileDate > latestDate) {
            latestDate = fileDate;
          }
        }
      });
      if (hasEntries) {
        return {
          fileName: getFileName(response.headers['content-disposition']),
          latestModified: latestDate ? latestDate.toISOString() : null,
        } as WintimeFolderResponse;
      }
      // Zip exists, but contains only directories
      return {
        fileName: 'rejected',
        latestModified: null,
      } as WintimeFolderResponse;
    }
    // No zip content
    return {
      fileName: 'rejected',
      latestModified: null,
    } as WintimeFolderResponse;
  } catch (error) {
    return {
      fileName: 'rejected',
      latestModified: null,
    } as WintimeFolderResponse;
  }
  return {
    fileName: 'Rejected',
    latestModified: null,
  } as WintimeFolderResponse;
});

// List WinTime backup files from Azure store
export const listWintimeBackupFilesAsync = createAsyncThunk(
  'auth/listWintimeBackupFiles',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        '/accountings/accounting/listwintimebackupfiles',
        {
          headers: {
            ...apiRequestHeaders.csrfHeader(),
            ...apiRequestHeaders.activeSiteHeader(),
          },
        },
      );
      // Backend returns ApiResponse<{ data: string[] }>
      return response.data.result as AzureFileItemDto[];
    } catch (error) {
      return rejectWithValue(error.response?.data ?? 'Failed to list files');
    }
  },
);

// Download selected WinTime backup files as ZIP
export const downloadSelectedWintimeBackupFilesAsync = createAsyncThunk(
  'auth/downloadSelectedWintimeBackupFiles',
  async (fileNames: string[], { rejectWithValue }) => {
    try {
      const response = await axios.post(
        '/accountings/accounting/downloadselectedwintimebackupfiles',
        { fileNames },
        {
          headers: {
            'Content-Type': 'application/json',
            ...apiRequestHeaders.csrfHeader(),
            ...apiRequestHeaders.activeSiteHeader(),
          },
          responseType: 'blob',
        },
      );
      const contentDisposition = response.headers['content-disposition'];
      const fileName = getFileName(contentDisposition);
      const blobUrl = URL.createObjectURL(response.data);
      return { blobUrl, fileName };
    } catch (error) {
      return rejectWithValue(
        error.response?.data ?? 'Failed to download files',
      );
    }
  },
);

export const generateWintimeFilesAsync = createAsyncThunk(
  'auth/generateWintimeFilesAsync',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        '/accountings/accounting/sendfilestowintime',
        {
          timeout: 900000,
          headers: {
            'Content-Type': 'application/zip',
            ...apiRequestHeaders.csrfHeader(),
            ...apiRequestHeaders.activeSiteHeader(),
          },
        },
      );
      return response.data.isSuccess;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);
// export const downloadFilesFromWintimeFolderAsync = createAsyncThunk<{
//   blob: Blob;
//   fileName: string;
// }>('auth/downloadFilesFromWintimeFolder', async (_, { rejectWithValue }) => {
//   try {
//     const data = await userService.fetchWintimeFilesAsZip();
//     return data;
//   } catch (error) {
//     return rejectWithValue(error.response.data);
//   }
// });

const identityUsersSlice = createSlice({
  name: 'users',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(getAllIdentityUsersAsync.pending, state => {
        state.identityUsersStatus = LOADING_STATUS.LOADING;
      })
      .addCase(getAllIdentityUsersAsync.fulfilled, (state, action) => {
        state.identityUsersStatus = LOADING_STATUS.IDLE;
        state.identityUsers = action.payload;
      })
      .addCase(getAllIdentityUsersAsync.rejected, state => {
        state.identityUsersStatus = LOADING_STATUS.FAILED;
      })
      .addCase(getIdentityUserDetailsAsync.pending, state => {
        state.identityUsersStatus = LOADING_STATUS.LOADING;
      })
      .addCase(getIdentityUserDetailsAsync.fulfilled, (state, action) => {
        state.identityUsersStatus = LOADING_STATUS.IDLE;
        state.identityDetails = action.payload;
      })
      .addCase(getIdentityUserDetailsAsync.rejected, state => {
        state.identityUsersStatus = LOADING_STATUS.FAILED;
      })
      .addCase(getUserDetailsByIdentityIdAsync.pending, state => {
        state.userStatus = LOADING_STATUS.LOADING;
      })
      .addCase(getUserDetailsByIdentityIdAsync.fulfilled, (state, action) => {
        state.userStatus = LOADING_STATUS.IDLE;
        state.userDetails = action.payload;
      })
      .addCase(getUserDetailsByIdentityIdAsync.rejected, state => {
        state.userStatus = LOADING_STATUS.FAILED;
      })
      .addCase(getIdentityUserRolesAsync.pending, state => {
        state.userStatus = LOADING_STATUS.LOADING;
      })
      .addCase(getIdentityUserRolesAsync.fulfilled, (state, action) => {
        state.userStatus = LOADING_STATUS.IDLE;
        state.identityUserRoles = action.payload;
      })
      .addCase(getIdentityUserRolesAsync.rejected, state => {
        state.userStatus = LOADING_STATUS.FAILED;
      })
      .addCase(getAllRolesAsync.pending, state => {
        state.rolesStatus = LOADING_STATUS.LOADING;
      })
      .addCase(getAllRolesAsync.fulfilled, (state, action) => {
        state.rolesStatus = LOADING_STATUS.IDLE;
        state.allRoles = action.payload;
      })
      .addCase(getAllRolesAsync.rejected, state => {
        state.rolesStatus = LOADING_STATUS.FAILED;
      })
      .addCase(getAccountRolesFromDbAsync.pending, state => {
        state.accountRolesLoadingStatus = LOADING_STATUS.LOADING;
      })
      .addCase(getAccountRolesFromDbAsync.fulfilled, (state, action) => {
        state.accountRolesLoadingStatus = LOADING_STATUS.IDLE;
        state.accountRoles = action.payload;
      })
      .addCase(getAccountRolesFromDbAsync.rejected, state => {
        state.accountRolesLoadingStatus = LOADING_STATUS.FAILED;
      })
      .addCase(getAllAccountsFromDbAsync.pending, state => {
        state.accountListLoadingStatus = LOADING_STATUS.LOADING;
      })
      .addCase(getAllAccountsFromDbAsync.fulfilled, (state, action) => {
        state.accountListLoadingStatus = LOADING_STATUS.IDLE;
        state.accountList = action.payload.data;
        state.accountPagination = PopulatePagination(action.payload);
      })
      .addCase(getAllAccountsFromDbAsync.rejected, state => {
        state.accommodationAccountListLoadingStatus = LOADING_STATUS.FAILED;
      })
      .addCase(getAccommodationAccountsFromDbAsync.pending, state => {
        state.accommodationAccountListLoadingStatus = LOADING_STATUS.LOADING;
      })
      .addCase(
        getAccommodationAccountsFromDbAsync.fulfilled,
        (state, action) => {
          state.accommodationAccountListLoadingStatus = LOADING_STATUS.IDLE;
          state.accommodationAccountList = action.payload.data;
          state.accountPagination = PopulatePagination(action.payload);
        },
      )
      .addCase(getAccommodationAccountsFromDbAsync.rejected, state => {
        state.accountListLoadingStatus = LOADING_STATUS.FAILED;
      })
      .addCase(getOwnerMaintainerAccountsAsync.pending, state => {
        state.ownerMaintainerAccountListLoadingStatus = LOADING_STATUS.LOADING;
      })
      .addCase(getOwnerMaintainerAccountsAsync.fulfilled, (state, action) => {
        state.ownerMaintainerAccountListLoadingStatus = LOADING_STATUS.IDLE;
        state.ownerMaintainerAccountList = action.payload.data;
        state.ownerMaintainerPagination = PopulatePagination(action.payload);
      })
      .addCase(getOwnerMaintainerAccountsAsync.rejected, state => {
        state.ownerMaintainerAccountListLoadingStatus = LOADING_STATUS.FAILED;
      })
      .addCase(getAllCustomersFromDbAsync.pending, state => {
        state.accountListLoadingStatus = LOADING_STATUS.LOADING;
      })
      .addCase(getAllCustomersFromDbAsync.fulfilled, (state, action) => {
        state.accountListLoadingStatus = LOADING_STATUS.IDLE;
        state.customerList = action.payload.data;
        state.customerPagination = PopulatePagination(action.payload);
      })
      .addCase(getAllCustomersFromDbAsync.rejected, state => {
        state.accountListLoadingStatus = LOADING_STATUS.FAILED;
      })
      .addCase(getAcountDetailsFromDbAsync.pending, state => {
        state.accountDetailsLoadingStatus = LOADING_STATUS.LOADING;
      })
      .addCase(getAcountDetailsFromDbAsync.fulfilled, (state, action) => {
        state.accountDetailsLoadingStatus = LOADING_STATUS.IDLE;
        state.accountDetails = action.payload;
      })
      .addCase(getAcountDetailsFromDbAsync.rejected, state => {
        state.accountDetailsLoadingStatus = LOADING_STATUS.FAILED;
      })
      .addCase(getIdentityAddressDetailsAsync.pending, state => {
        state.addressLoadingStatus = LOADING_STATUS.LOADING;
      })
      .addCase(getIdentityAddressDetailsAsync.fulfilled, (state, action) => {
        state.addressLoadingStatus = LOADING_STATUS.IDLE;
        state.addressDetails = action.payload;
      })
      .addCase(getIdentityAddressDetailsAsync.rejected, state => {
        state.addressLoadingStatus = LOADING_STATUS.FAILED;
      })
      .addCase(getOmaLiittymaSettingsAsync.pending, state => {
        state.omaLiittymaLoadingStatus = LOADING_STATUS.LOADING;
      })
      .addCase(getOmaLiittymaSettingsAsync.fulfilled, (state, action) => {
        state.omaLiittymaLoadingStatus = LOADING_STATUS.IDLE;
        state.omaLiittymaData = action.payload;
      })
      .addCase(getOmaLiittymaSettingsAsync.rejected, state => {
        state.omaLiittymaLoadingStatus = LOADING_STATUS.FAILED;
      })
      .addCase(getBookingNoteSettingsAsync.pending, state => {
        state.bookingNoteSettingsLoadingStatus = LOADING_STATUS.LOADING;
      })
      .addCase(getBookingNoteSettingsAsync.fulfilled, (state, action) => {
        state.bookingNoteSettingsLoadingStatus = LOADING_STATUS.IDLE;
        state.bookingNoteSettingsData = action.payload;
      })
      .addCase(getBookingNoteSettingsAsync.rejected, state => {
        state.bookingNoteSettingsLoadingStatus = LOADING_STATUS.FAILED;
      })
      .addCase(getVatSettingsAsync.pending, state => {
        state.vatSettingsLoadingStatus = LOADING_STATUS.LOADING;
      })
      .addCase(getVatSettingsAsync.fulfilled, (state, action) => {
        state.vatSettingsLoadingStatus = LOADING_STATUS.IDLE;
        state.vatSettingsData = action.payload;
      })
      .addCase(getVatSettingsAsync.rejected, state => {
        state.vatSettingsLoadingStatus = LOADING_STATUS.FAILED;
      })
      .addCase(getBankSettingsAsync.pending, state => {
        state.bankSettingsLoadingStatus = LOADING_STATUS.LOADING;
      })
      .addCase(getBankSettingsAsync.fulfilled, (state, action) => {
        state.bankSettingsLoadingStatus = LOADING_STATUS.IDLE;
        state.bankSettingsData = action.payload;
      })
      .addCase(getBankSettingsAsync.rejected, state => {
        state.bankSettingsLoadingStatus = LOADING_STATUS.FAILED;
      })
      .addCase(getTarmoSettingsAsync.pending, state => {
        state.tarmoSettingsLoadingStatus = LOADING_STATUS.LOADING;
      })
      .addCase(getTarmoSettingsAsync.fulfilled, (state, action) => {
        state.tarmoSettingsLoadingStatus = LOADING_STATUS.IDLE;
        state.tarmoSettingsData = action.payload;
      })
      .addCase(getTarmoSettingsAsync.rejected, state => {
        state.tarmoSettingsLoadingStatus = LOADING_STATUS.FAILED;
      })
      .addCase(getCustomerSettingsAsync.pending, state => {
        state.customerSettingsLoadingStatus = LOADING_STATUS.LOADING;
      })
      .addCase(getCustomerSettingsAsync.fulfilled, (state, action) => {
        state.customerSettingsLoadingStatus = LOADING_STATUS.IDLE;
        state.customerSettingsData = action.payload;
      })
      .addCase(getCustomerSettingsAsync.rejected, state => {
        state.customerSettingsLoadingStatus = LOADING_STATUS.FAILED;
      })
      .addCase(getOfficerSettingsAsync.pending, state => {
        state.accountRolesLoadingStatus = LOADING_STATUS.LOADING;
      })
      .addCase(getOfficerSettingsAsync.fulfilled, (state, action) => {
        state.accountRolesLoadingStatus = LOADING_STATUS.IDLE;
        state.officerSettings = action.payload;
      })
      .addCase(getOfficerSettingsAsync.rejected, state => {
        state.accountRolesLoadingStatus = LOADING_STATUS.FAILED;
      })
      .addCase(updateOfficerSettingsAsync.fulfilled, (state, action) => {
        if (state.officerSettings) {
          state.officerSettings.agentNumber = action.meta.arg.agentNumber;
        }
      })
      .addCase(getAllCustomerGroupsAsync.pending, state => {
        state.customerGroupsStatus = LOADING_STATUS.LOADING;
      })
      .addCase(getAllCustomerGroupsAsync.fulfilled, (state, action) => {
        state.customerGroupsStatus = LOADING_STATUS.IDLE;
        state.customerGroupItems = action.payload.data;
        state.customerGroupPagination = PopulatePagination(action.payload);
      })
      .addCase(getAllCustomerGroupsAsync.rejected, state => {
        state.customerGroupsStatus = LOADING_STATUS.FAILED;
      })
      .addCase(getDefaultCustomerGroupAsync.pending, state => {
        state.customerGroupsStatus = LOADING_STATUS.LOADING;
      })
      .addCase(getDefaultCustomerGroupAsync.fulfilled, (state, action) => {
        state.customerGroupsStatus = LOADING_STATUS.IDLE;
        state.defaultCustomerGroupItem = action.payload;
        state.customerGroupPagination = PopulatePagination(action.payload);
      })
      .addCase(getDefaultCustomerGroupAsync.rejected, state => {
        state.customerGroupsStatus = LOADING_STATUS.FAILED;
      })
      .addCase(getCustomerGroupDetailsAsync.pending, state => {
        state.customerGroupDetailsStatus = LOADING_STATUS.LOADING;
      })
      .addCase(getCustomerGroupDetailsAsync.fulfilled, (state, action) => {
        state.customerGroupDetailsStatus = LOADING_STATUS.IDLE;
        state.customerGroupDetailsData = action.payload;
      })
      .addCase(getCustomerGroupDetailsAsync.rejected, state => {
        state.customerGroupDetailsStatus = LOADING_STATUS.FAILED;
      })
      .addCase(addUserPrivateLoginAsync.fulfilled, (state, action) => {
        const accountId = action.payload;

        // find the account and update its state in the redux store
        const accountIndex = state.accountList.findIndex(
          account => account.id === accountId,
        );

        if (accountIndex !== -1) {
          state.accountList[accountIndex].hasUserLogin = true;
        }
      })
      .addCase(deleteUserLoginAsync.fulfilled, (state, action) => {
        const accountId = action.payload;

        // find the account and update its state in the redux store
        const accountIndex = state.accountList.findIndex(
          account => account.id === accountId,
        );

        if (accountIndex !== -1) {
          state.accountList[accountIndex].hasUserLogin = false;
        }
      })
      .addCase(getCustomerBookingsAsync.pending, state => {
        state.customerBookingsStatus = LOADING_STATUS.LOADING;
      })
      .addCase(getCustomerBookingsAsync.fulfilled, (state, action) => {
        state.customerBookingsStatus = LOADING_STATUS.IDLE;
        state.customerBookings = action.payload.data;
        state.customerBookingsPagination = PopulatePagination(action.payload);
      })
      .addCase(getCustomerBookingsAsync.rejected, state => {
        state.customerBookingsStatus = LOADING_STATUS.FAILED;
      })
      .addCase(getAllOwnerBookingsAsync.pending, state => {
        state.ownerBookingsStatus = LOADING_STATUS.LOADING;
      })
      .addCase(getAllOwnerBookingsAsync.fulfilled, (state, action) => {
        state.ownerBookingsStatus = LOADING_STATUS.IDLE;
        state.ownerBookings = action.payload.data;
        state.ownerBookingsPagination = PopulatePagination(action.payload);
      })
      .addCase(getAllOwnerBookingsAsync.rejected, state => {
        state.ownerBookingsStatus = LOADING_STATUS.FAILED;
      })
      .addCase(getAllMaintainerBookingsAsync.pending, state => {
        state.maintainerBookingsStatus = LOADING_STATUS.LOADING;
      })
      .addCase(getAllMaintainerBookingsAsync.fulfilled, (state, action) => {
        state.maintainerBookingsStatus = LOADING_STATUS.IDLE;
        state.maintainerBookings = action.payload.data;
        state.maintainerBookingsPagination = PopulatePagination(action.payload);
      })
      .addCase(getAllMaintainerBookingsAsync.rejected, state => {
        state.maintainerBookingsStatus = LOADING_STATUS.FAILED;
      })
      .addCase(downloadFilesFromWintimeFolderAsync.pending, state => {
        state.downloadwintimeFileStatus = LOADING_STATUS.LOADING;
      })
      .addCase(
        downloadFilesFromWintimeFolderAsync.fulfilled,
        (state, action) => {
          state.downloadwintimeFileStatus = LOADING_STATUS.IDLE;
          state.wintimeFile.blobUrl = action.payload.blobUrl;
          state.wintimeFile.fileName = action.payload.fileName;
          // Handle the downloaded file data if needed
        },
      )
      .addCase(
        downloadFilesFromWintimeFolderAsync.rejected,
        (state, action) => {
          state.downloadwintimeFileStatus = LOADING_STATUS.FAILED;
        },
      )
      .addCase(resetAccountDetailsAsync.fulfilled, (state, action) => {
        state.accountDetails = action.payload;
      })
      .addCase(getPersonalIdentityDataAsync.pending, state => {
        state.personalIdentityLoadingStatus = LOADING_STATUS.LOADING;
      })
      .addCase(getPersonalIdentityDataAsync.fulfilled, (state, action) => {
        state.personalIdentityLoadingStatus = LOADING_STATUS.IDLE;
        state.personalIdentityData = action.payload;
      })
      .addCase(getPersonalIdentityDataAsync.rejected, state => {
        state.personalIdentityLoadingStatus = LOADING_STATUS.FAILED;
      })
      .addCase(savePersonalIdentityAsync.pending, state => {
        state.personalIdentityLoadingStatus = LOADING_STATUS.LOADING;
      })
      .addCase(savePersonalIdentityAsync.fulfilled, state => {
        state.personalIdentityLoadingStatus = LOADING_STATUS.IDLE;
      })
      .addCase(savePersonalIdentityAsync.rejected, state => {
        state.personalIdentityLoadingStatus = LOADING_STATUS.FAILED;
      });
  },
});
export const selectIdentityUsers = (state: RootState) =>
  state.identityusers.identityUsers;

export const selectIdentityUserDetails = (state: RootState) =>
  state.identityusers.identityDetails;
export const selectIdentityUsersLoadingStatus = (state: RootState) =>
  state.identityusers.identityUsersStatus;

export const selectUsersLoadingStatus = (state: RootState) =>
  state.identityusers.userStatus;

export const selectUserDetails = (state: RootState) =>
  state.identityusers.userDetails;

export const selectAllRoles = (state: RootState) =>
  state.identityusers.allRoles;

export const selectUserRoles = (state: RootState) =>
  state.identityusers.accountRoles;

export const selectBankSettings = (state: RootState) =>
  state.identityusers.bankSettingsData;

export const selectTarmoSettings = (state: RootState) =>
  state.identityusers.tarmoSettingsData;

export const selectRolesLoadingStatus = (state: RootState) =>
  state.identityusers.rolesStatus;

export const selectbankSettingsLoadingStatus = (state: RootState) =>
  state.identityusers.bankSettingsLoadingStatus;

export const selectTarmoSettingsLoadingStatus = (state: RootState) =>
  state.identityusers.tarmoSettingsLoadingStatus;

export const selectUserRolesLoadingStatus = (state: RootState) =>
  state.identityusers.accountRolesLoadingStatus;

export const selectAllAccountsLoadingStatus = (state: RootState) =>
  state.identityusers.accountListLoadingStatus;

export const selectOwnerAndMaintainerLoadingStatus = (state: RootState) =>
  state.identityusers.ownerMaintainerAccountListLoadingStatus;

export const selectAccommodationAccountsLoadingStatus = (state: RootState) =>
  state.identityusers.accommodationAccountListLoadingStatus;
export const selectAllAccountsFromDb = (state: RootState) =>
  state.identityusers.accountList;

export const selectAccommodationAccounts = (state: RootState) =>
  state.identityusers.accommodationAccountList;
export const selectAllAccountsPagination = (state: RootState) =>
  state.identityusers.accountPagination;

export const selectOwnerBookingsPagination = (state: RootState) =>
  state.identityusers.ownerBookingsPagination;

export const selectMaintainerBookingsPagination = (state: RootState) =>
  state.identityusers.maintainerBookingsPagination;

export const selectAllCustomersLoadingStatus = (state: RootState) =>
  state.identityusers.accountListLoadingStatus;

export const selectAllCustomersFromDb = (state: RootState) =>
  state.identityusers.customerList;

export const selectOwnerMaintainerAccounts = (state: RootState) =>
  state.identityusers.ownerMaintainerAccountList;

export const selectOwnerMaintainerAccountsLoadingStatus = (state: RootState) =>
  state.identityusers.ownerMaintainerAccountListLoadingStatus;
export const selectAllCustomersPagination = (state: RootState) =>
  state.identityusers.customerPagination;

export const selectPersonalIdentityData = (state: RootState) =>
  state.identityusers.personalIdentityData;
export const selectPersonalIdentityLoadingStatus = (state: RootState) =>
  state.identityusers.personalIdentityLoadingStatus;

export const selectAccountDetailsLoadingStatus = (state: RootState) =>
  state.identityusers.accountDetailsLoadingStatus;

export const selectAccountDetailsFromDb = (state: RootState) =>
  state.identityusers.accountDetails;

export const selectAddressDetailsFromDb = (state: RootState) =>
  state.identityusers.addressDetails;

export const selectOmaLiittymaData = (state: RootState) =>
  state.identityusers.omaLiittymaData;

export const selectBookingNoteData = (state: RootState) =>
  state.identityusers.bookingNoteSettingsData;

export const selectVatData = (state: RootState) =>
  state.identityusers.vatSettingsData;
export const selectOmaLiittymaLoadingStatus = (state: RootState) =>
  state.identityusers.omaLiittymaLoadingStatus;

export const selectBookingNoteLoadingStatus = (state: RootState) =>
  state.identityusers.bookingNoteSettingsLoadingStatus;

export const selectVatLoadingStatus = (state: RootState) =>
  state.identityusers.vatSettingsLoadingStatus;
export const selectAddressDetailsLoadingStatus = (state: RootState) =>
  state.identityusers.addressLoadingStatus;

export const selectOfficerSettingsLoadingStatus = (state: RootState) =>
  state.identityusers.officerSettingsLoadingStatus;

export const selectOfficerSettingsData = (state: RootState) =>
  state.identityusers.officerSettings;

export const selectCustomerGroups = (state: RootState) =>
  state.identityusers.customerGroupItems;
export const selectDefaultCustomerGroup = (state: RootState) =>
  state.identityusers.defaultCustomerGroupItem;
export const selectCustomerGroupsPagination = (state: RootState) =>
  state.identityusers.customerGroupPagination;
export const selectCustomerGroupsLoadingStatus = (state: RootState) =>
  state.identityusers.customerGroupsStatus;

export const selectCustomerGroupDetails = (state: RootState) =>
  state.identityusers.customerGroupDetailsData;
export const selectCustomerGroupDetailsLoadingStatus = (state: RootState) =>
  state.identityusers.customerGroupDetailsStatus;

export const selectCustomerSettings = (state: RootState) =>
  state.identityusers.customerSettingsData;

export const selectOwnerBookings = (state: RootState) =>
  state.identityusers.ownerBookings;

export const selectMaintainerBookings = (state: RootState) =>
  state.identityusers.maintainerBookings;

export const selectOwnerBookingsStatus = (state: RootState) =>
  state.identityusers.ownerBookingsStatus;

export const selectCustomerBookingsStatus = (state: RootState) =>
  state.identityusers.customerBookingsStatus;

export const selectCustomerBookings = (state: RootState) =>
  state.identityusers.customerBookings;

export const selectCustomerBookingsPagination = (state: RootState) =>
  state.identityusers.customerBookingsPagination;

export const selectMaintainerBookingsStatus = (state: RootState) =>
  state.identityusers.maintainerBookingsStatus;
export const selectCustomerSettingsLoadingStatus = (state: RootState) =>
  state.identityusers.customerSettingsLoadingStatus;

export const selectWintimeLoadingStatusLoadingStatus = (state: RootState) =>
  state.identityusers.customerSettingsLoadingStatus;

export const selectWintimeFile = (state: RootState) =>
  state.identityusers.wintimeFile;

/*
export const selectCaretakerRoles = createSelector(
  (state: RootState) => state.identityusers.allRoles?.rolesList,
  rolesList =>
    rolesList?.filter(role =>
      Object.values(CARETAKER_ROLES).includes(role.id as CARETAKER_ROLES),
    ),
);
*/
export const selectCaretakerRoles = createSelector(
  (state: RootState) => state.identityusers.allRoles?.rolesList,
  rolesList => {
    return rolesList?.filter(role =>
      Object.values(CARETAKER_ROLES).includes(role.id as CARETAKER_ROLES),
    );
  },
);

export const selectOfficerRoles = createSelector(
  (state: RootState) => state.identityusers.allRoles?.rolesList,
  rolesList =>
    rolesList?.filter(role =>
      Object.values(OFFICER_ROLES).includes(role.id as OFFICER_ROLES),
    ),
);

export const selectCustomerRole = createSelector(
  (state: RootState) => state.identityusers.allRoles?.rolesList,
  rolesList => rolesList?.filter(role => role.id === CUSTOMER_ROLE.Customer),
);

export const selectOwnerRole = createSelector(
  (state: RootState) => state.identityusers.allRoles?.rolesList,
  rolesList => rolesList?.filter(role => role.id === OWNER_ROLE.Customer),
);

export const selectTravelAgencyRole = createSelector(
  (state: RootState) => state.identityusers.allRoles?.rolesList,
  rolesList =>
    rolesList?.filter(role => role.id === TRAVELAGENCY_ROLE.Customer),
);

export default identityUsersSlice.reducer;
